import React, { useState, useEffect } from 'react';
import { Plus, Search, Building2, Edit2, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';

// Helper function to handle empty select values
const selectValue = (val) => val || "none";
const fromSelectValue = (val) => val === "none" ? "" : val;
import { toast } from 'sonner';
import { clientService } from '../services/api';
import { useAuth } from '../context/AuthContext';

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const { hasRole } = useAuth();

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    address: '',
    city: '',
    province: '',
    country: 'Italy',
    contact_person: '',
    email: '',
    phone: '',
    is_also_supplier: false
  });

  useEffect(() => {
    loadClients();
  }, [search]);

  const loadClients = async () => {
    try {
      const response = await clientService.getAll({ search, is_active: true });
      setClients(response.data);
    } catch (error) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClient) {
        await clientService.update(editingClient.id, formData);
        toast.success('Client updated successfully');
      } else {
        await clientService.create(formData);
        toast.success('Client created successfully');
      }
      setDialogOpen(false);
      resetForm();
      loadClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      code: client.code,
      name: client.name,
      address: client.address || '',
      city: client.city || '',
      province: client.province || '',
      country: client.country || 'Italy',
      contact_person: client.contact_person || '',
      email: client.email || '',
      phone: client.phone || '',
      is_also_supplier: client.is_also_supplier || false
    });
    setDialogOpen(true);
  };

  const handleDelete = async (client) => {
    if (window.confirm(`Are you sure you want to deactivate ${client.name}?`)) {
      try {
        await clientService.delete(client.id);
        toast.success('Client deactivated');
        loadClients();
      } catch (error) {
        toast.error('Failed to deactivate client');
      }
    }
  };

  const resetForm = () => {
    setEditingClient(null);
    setFormData({
      code: '',
      name: '',
      address: '',
      city: '',
      province: '',
      country: 'Italy',
      contact_person: '',
      email: '',
      phone: '',
      is_also_supplier: false
    });
  };

  const openNewDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Clienti</h1>
          <p className="text-sm text-slate-500 mt-1">Gestisci l'anagrafica clienti</p>
        </div>
        {hasRole('admin', 'quality', 'production') && (
          <Button onClick={openNewDialog} data-testid="add-client-btn">
            <Plus className="w-4 h-4 mr-2" />
            Aggiungi Cliente
          </Button>
        )}
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Cerca per codice o nome..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-clients-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : clients.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No clients found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                    <th>City</th>
                    <th>Contact</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((client) => (
                    <tr key={client.id} data-testid={`client-row-${client.id}`}>
                      <td className="font-mono text-sm font-medium">{client.code}</td>
                      <td className="font-medium">{client.name}</td>
                      <td>{client.city || '-'}</td>
                      <td>{client.contact_person || '-'}</td>
                      <td className="text-sm text-slate-500">{client.email || '-'}</td>
                      <td className="text-sm text-slate-500">{client.phone || '-'}</td>
                      <td>
                        <Badge className={client.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}>
                          {client.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td>
                        {hasRole('admin', 'quality', 'production') && (
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleEdit(client)} data-testid={`edit-client-${client.id}`}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            {hasRole('admin') && (
                              <Button variant="ghost" size="sm" onClick={() => handleDelete(client)} data-testid={`delete-client-${client.id}`}>
                                <Trash2 className="w-4 h-4 text-rose-500" />
                              </Button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingClient ? 'Edit Client' : 'New Client'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="code">Code *</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  required
                  disabled={!!editingClient}
                  data-testid="client-code-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Company Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="client-name-input"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="province">Province</Label>
                <Input
                  id="province"
                  value={formData.province}
                  onChange={(e) => setFormData({ ...formData, province: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact_person">Contact Person</Label>
                <Input
                  id="contact_person"
                  value={formData.contact_person}
                  onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
            </div>
            <div className="mt-3 rounded-md border border-blue-200 bg-blue-50/50 p-3 flex items-start gap-2">
              <input
                type="checkbox"
                id="is_also_supplier"
                className="mt-1"
                checked={!!formData.is_also_supplier}
                onChange={e => setFormData({ ...formData, is_also_supplier: e.target.checked })}
                data-testid="input-is-also-supplier"
              />
              <div>
                <Label htmlFor="is_also_supplier" className="text-sm font-medium cursor-pointer">Anche fornitore</Label>
                <p className="text-xs text-slate-600 mt-0.5">
                  Se attivo, viene creato/aggiornato automaticamente un record Fornitore collegato (codice <code>SUP-CLI-{formData.code || 'XXX'}</code>).
                  Sarà selezionabile nel dropdown "Fornitore" dell&apos;Arrivo Grezzo.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="save-client-btn">
                {editingClient ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
